import Typography, { type TypographyProps } from '@ixigo/iui/Typography';
import cn from '@repo/shared/utils/cn';
import type { FareSummaryItem } from '@/api';

export interface LineItemProps
	extends React.ComponentPropsWithoutRef<'div'>,
		Omit<FareSummaryItem, 'label'> {
	/** Label */
	label: string | React.ReactNode;
	labelWeight?: TypographyProps['fontWeight'];
	/** Label color */
	labelColor?: string;
	/** Value color */
	valueColor?: string;
	valueWeight?: TypographyProps['fontWeight'];
	/** Discount value color */
	discountValueColor?: string;
	/** Optional left icon */
	leftIcon?: React.ReactNode;
	/** Optional right icon (next to the label) */
	rightIcon?: React.ReactNode;
	/** Size of the item */
	/**
	 * @default "md"
	 */
	size?: 'md' | 'lg';
	/** Custom class name */
	className?: string;
}
type SizeMap = {
	[K in 'md' | 'lg']: {
		[P in 'label' | 'discountValue' | 'value']: {
			variant: TypographyProps['variant'];
			fontWeight?: TypographyProps['fontWeight'];
		};
	};
};

const sizeMap: SizeMap = {
	md: {
		label: {
			variant: 'body-md',
		},
		discountValue: {
			variant: 'body-xs',
		},
		value: {
			variant: 'body-md',
			fontWeight: 'medium',
		},
	},
	lg: {
		label: {
			variant: 'h5',
			fontWeight: 'medium',
		},
		discountValue: {
			variant: 'body-sm',
		},
		value: {
			variant: 'h5',
			fontWeight: 'bold',
		},
	},
};

/**
 * LineItem Component
 *
 * A reusable component for individual fare summary rows.
 * Supports icons, strikethrough pricing, and discount styling.
 */
export function LineItem({
	label,
	labelWeight = 'normal',
	value,
	valueWeight = 'normal',
	discountValue,
	leftIcon,
	rightIcon,
	className,
	size = 'md',
	labelColor,
	valueColor,
	discountValueColor = 'text-secondary',
	...props
}: LineItemProps) {
	return (
		<div
			className={cn('flex items-center justify-between py-5 gap-5', className)}
			{...props}
		>
			{/* Left Section - Icon + Label + Right Icon */}
			<div className="flex items-center gap-5 min-w-0 flex-1">
				{/* Left Icon */}
				{leftIcon && (
					<div className="flex-shrink-0 flex items-center justify-center">
						{leftIcon}
					</div>
				)}

				{/* Label and Right Icon */}
				<div className="flex items-center gap-0.5 min-w-0">
					{typeof label === 'string' ? (
						<Typography
							variant={sizeMap[size].label.variant}
							fontWeight={sizeMap[size].label.fontWeight || labelWeight}
							className={cn('truncate', labelColor)}
						>
							{label}
						</Typography>
					) : (
						label
					)}
					{rightIcon && (
						<div className="flex-shrink-0 flex items-center justify-center">
							{rightIcon}
						</div>
					)}
				</div>
			</div>

			{/* Right Section - Pricing */}
			<div className="flex items-baseline gap-0.5 text-right">
				{discountValue && (
					<Typography
						variant={sizeMap[size].discountValue.variant}
						fontWeight={sizeMap[size].discountValue.fontWeight}
						className={cn(
							'truncate line-through decoration-current inline-block align-baseline',
							discountValueColor,
						)}
					>
						{value}
					</Typography>
				)}
				<Typography
					variant={sizeMap[size].value.variant}
					fontWeight={sizeMap[size].value.fontWeight || valueWeight}
					className={cn('truncate', valueColor)}
				>
					{discountValue || value}
				</Typography>
			</div>
		</div>
	);
}

export default LineItem;
